import React, { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Pagination } from 'components/molecules/Pagination';
import { Table } from 'components/molecules/Table';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { Loader } from 'components/atoms/Loader';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EPerPages, ISortOptions, mapToEPerPages } from 'models/table';
import { objectToQueryString, prepareQueryValues } from 'helpers/searchQuery';
import { joinValuesToString } from 'helpers/joinValuesToString';
import { replaceUrlIfOnPage } from 'utils/history';
import { useCaseManagementTasks } from './hooks';
import { useDictionary } from 'models/dictionaryContext';
import { useAccount } from 'pages/AccountPage/hooks';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { ERouteLinks } from 'models/route';
import AssignCaseForm from '../CasesPage/CaseDetailsPage/AssignCaseForm';
import { Box } from '@mui/material';
import { mapCasesToDataSource, tasksTableColumns } from './helpers';
import {
  ISearchFormValues,
  TasksSearchForm,
  initialApplicationsSearchFormValues,
} from './TasksSearchForm';
import { CardList } from 'components/molecules/CardList';
import { TasksCard } from './TasksCard';

const TASKS_TAB_QUERY = 'task';

const Task = () => {
  const { t } = useTranslations();
  const { isDesktop } = useThemeBreakpoint();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { shortDateFormat, getProperties } = useGlobalProperty();
  const { clearAlert, samePageAlert } = useAlert();

  const initialFormValues = useMemo(() => {
    const queryValues = prepareQueryValues(
      initialApplicationsSearchFormValues,
      new URLSearchParams(searchParams),
    );
    return queryValues ? { ...initialApplicationsSearchFormValues, ...queryValues } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildTasksQueryPath = (
    values: ISearchFormValues | undefined,
    page: number,
    size: EPerPages,
  ): string => {
    const queryString = objectToQueryString({
      ...(values ?? {}),
      assignedUsersUniqueIds: joinValuesToString(values?.assignedUsersUniqueIds),
      caseStatusesUniqueIds: joinValuesToString(values?.caseStatusesUniqueIds),
      licenceAuthoritiesUniqueIds: joinValuesToString(values?.licenceAuthoritiesUniqueIds),
      perPage: size,
      currentPage: page,
    });
    return `${pathname}?expand=${TASKS_TAB_QUERY}&${queryString}`;
  };

  const [currentPage, setCurrentPage] = useState<number>(() =>
    Number.parseInt(searchParams.get('currentPage') || '1', 10),
  );
  const [perPage, setPerPage] = useState<EPerPages>(() =>
    mapToEPerPages(searchParams.get('perPage') || EPerPages.perPage25),
  );
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [searchFormData, setSearchFormData] = useState<ISearchFormValues | undefined>(
    () => initialFormValues ?? undefined,
  );
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [assignCaseId, setAssignCaseId] = useState<string | null>(null);
  const { tasks, paginator, getCaseManagementTasksData } = useCaseManagementTasks();
  const { account, getSelfAccountData } = useAccount();
  const { canAssignCase } = usePrivilegesValidation();
  const {
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
    caseProcessTypes,
    getCaseProcessTypes,
    weaponLicenceCaseStatus,
    getWeaponLicenceCaseStatus,
    region,
    getRegion,
    otherRequestTaskName,
    getOtherRequestTaskName,
  } = useDictionary();

  const scopedWeaponLicencingAuthority = React.useMemo(() => {
    if (!weaponLicencingAuthority) {
      return weaponLicencingAuthority;
    }
    const assignedIds = account?.licenceAuthorityIds ?? [];
    if (!assignedIds.length) {
      return weaponLicencingAuthority;
    }
    return weaponLicencingAuthority.filter((authority) =>
      assignedIds.includes(authority.uniqueId as string),
    );
  }, [weaponLicencingAuthority, account]);

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    getSelfAccountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!weaponLicencingAuthority) {
      getWeaponLicencingAuthority();
    }
    if (!caseProcessTypes) {
      getCaseProcessTypes();
    }
    if (!weaponLicenceCaseStatus) {
      getWeaponLicenceCaseStatus();
    }
    if (!region) {
      getRegion();
    }
    if (!otherRequestTaskName) {
      getOtherRequestTaskName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    replaceUrlIfOnPage(buildTasksQueryPath(searchFormData, currentPage, perPage));
    (async () => {
      setIsPaginationLoading(true);
      if (!tasks.length) {
        setIsLoading(true);
      }
      await getCaseManagementTasksData(currentPage, perPage, searchFormData, sort);
      setIsLoading(false);
      setIsPaginationLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, sort, searchFormData]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleShowDetails = (id: string) => {
    navigate(`${ERouteLinks.CasesDetailsPage.replace(':caseId', id)}`);
  };

  const handleAssign = (id: string) => setAssignCaseId(id);
  const updateData = async () =>
    await getCaseManagementTasksData(currentPage, perPage, searchFormData, sort);

  return (
    <>
      <TasksSearchForm
        onSubmit={(arg) => {
          setSearchFormData(arg);
          setCurrentPage(1);
        }}
        initialFormValues={initialFormValues ?? undefined}
        weaponLicencingAuthority={scopedWeaponLicencingAuthority}
        caseProcessTypes={caseProcessTypes}
        weaponLicenceCaseStatus={weaponLicenceCaseStatus}
        region={region}
      />

      {assignCaseId && (
        <AssignCaseForm
          isOpen={!!assignCaseId}
          close={() => setAssignCaseId(null)}
          updateData={updateData}
          caseId={assignCaseId}
          currentWorkerId={
            tasks.find((item) => item.uniqueId === assignCaseId)?.assignedToUniqueId ??
            undefined
          }
        />
      )}

      {samePageAlert && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}

      {isLoading ? (
        <Loader isLoading />
      ) : (
        <>
          {isDesktop ? (
            <Table
              columns={tasksTableColumns({
                handleShowDetails,
                handleAssign,
                canAssignCase,
                t,
              })}
              dataSource={mapCasesToDataSource(
                tasks,
                shortDateFormat,
                otherRequestTaskName,
                weaponLicencingAuthority,
                weaponLicenceCaseStatus,
              )}
              onSort={setSort}
              id="cases_page_list-table"
            />
          ) : (
            <CardList
              items={mapCasesToDataSource(
                tasks,
                shortDateFormat,
                otherRequestTaskName,
                weaponLicencingAuthority,
                weaponLicenceCaseStatus,
              )}
              render={(data, index) => (
                <TasksCard
                  data={data}
                  key={index}
                  handleView={handleShowDetails}
                  handleAssign={handleAssign}
                  canAssignCase={canAssignCase}
                />
              )}
            />
          )}
        </>
      )}

      {paginator && (
        <Pagination
          count={paginator.totalElements}
          onChangePage={(page) => setCurrentPage(page)}
          current={currentPage}
          perPage={perPage}
          onChangePerPage={(value) => setPerPage(value)}
          isLoading={isPaginationLoading}
        />
      )}
    </>
  );
};

export default Task;
